import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import * as Y from 'yjs';
import { IconButton, Icon, Text, Tooltip, Divider, icon } from '@nula/core-ui';
import { canvasShortcuts } from '@/features/canvas/utils/canvas-shortcuts';
import { EdgeFormattingToolbar } from './edge-formatting-toolbar';
import { setEdgeLocked } from '@/features/canvas/engine/edges/set-edge-locked';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import {
  EDGE_ACTIONS,
  type EdgeAction,
  type EdgeActionContext,
} from '../../wiring/actions/edge-actions';
import styles from './node-floating-toolbar.module.css';

const HOLD_DURATION = 800;

function tooltipWithShortcut(label: string, shortcut: string): string {
  return shortcut ? `${label} · ${shortcut}` : label;
}

export interface EdgeFloatingToolbarProps {
  doc: Y.Doc;
  clientId: number;
  edgeId: string;
  onDelete: (edgeId: string) => void;
  /** Click handler for the inline-label "T" button. Bumps the edit
   *  token in the parent so the EdgeLabel input opens. */
  onEditLabel?: () => void;
  className?: string;
}

/**
 * Floating context toolbar for a selected edge. Mirrors NodeFloatingToolbar
 * — same `styles.root` / `styles.btn` / `styles.divider` conventions — so
 * the floating chrome is consistent across nodes and edges.
 *
 * v1 controls:
 *   - Visual / Logical kind chip (lifted EdgeKindPicker)
 *   - Delete
 *
 * Forthcoming (steps 5b/6):
 *   - Color, edge-style popover (stroke + curve)
 *   - Start / End direction marker dropdowns
 *   - Inline label trigger
 *   - Lock
 */
export function EdgeFloatingToolbar({
  doc,
  clientId,
  edgeId,
  onDelete,
  onEditLabel,
  className,
}: EdgeFloatingToolbarProps) {
  const binding = useCanvasBinding();
  const edge = canvasRoots(doc, binding).edges.get(edgeId);
  const locked = edge?.get('locked') === true;

  const toggleLock = useCallback(() => {
    setEdgeLocked(doc, clientId, edgeId, !locked, binding);
  }, [doc, clientId, edgeId, locked, binding]);

  // Hold-to-unlock: same 800ms gesture as NodeFloatingToolbar.
  const [holding, setHolding] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleHoldStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setHolding(true);
    timerRef.current = setTimeout(() => {
      setHolding(false);
      toggleLock();
    }, HOLD_DURATION);
  };
  const handleHoldEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHolding(false);
  };

  if (!edge) return null;

  if (locked) {
    return (
      <div className={clsx(styles.root, className)} role="toolbar" aria-label="Edge actions">
        <button
          type="button"
          className={`${styles.holdBtn} nopan nodrag`}
          style={{ '--hold-duration': `${HOLD_DURATION}ms` } as CSSProperties}
          aria-label="Hold to unlock"
          onMouseDown={handleHoldStart}
          onMouseUp={handleHoldEnd}
          onMouseLeave={handleHoldEnd}
          onTouchStart={handleHoldStart}
          onTouchEnd={handleHoldEnd}
        >
          <Icon name="lock" size={icon.md} fill />
          <Text size="sm" className={styles.holdLabel}>Hold to unlock</Text>
          {holding && <span className={styles.holdProgress} />}
        </button>
      </div>
    );
  }

  // Build the registry context. The locked branch above already
  // short-circuited via the hand-rolled hold-to-unlock button, so
  // this branch is always !locked — but we still pass the actual
  // value for completeness (and so registry's available() filter
  // could safely drop entries either way).
  const ctx: EdgeActionContext = {
    edgeId,
    locked,
    // No-op stubs for the z-order entries — TOOLBAR_EDGE_ACTION_IDS
    // filter excludes them so run() never reaches these.
    onBringToFront: () => {},
    onBringForward: () => {},
    onSendBackward: () => {},
    onSendToBack: () => {},
    onToggleLock: () => toggleLock(),
    onDelete: (id) => onDelete(id),
  };

  // Strict subset surfaced on the floating toolbar — z-order is
  // menu-only for edges too.
  const visibleActions = EDGE_ACTIONS
    .filter((a) => TOOLBAR_EDGE_ACTION_IDS.has(a.id))
    .filter((a) => a.available(ctx));

  return (
    <div className={clsx(styles.root, className)} role="toolbar" aria-label="Edge actions">
      <EdgeFormattingToolbar doc={doc} clientId={clientId} edgeId={edgeId} />

      {onEditLabel && (
        <Tooltip title="Add label" placement="top">
          <IconButton
            transparent
            aria-label="Add label"
            className={styles.btn}
            onClick={onEditLabel}
          >
            <Icon name="text_fields" size={icon.md} />
          </IconButton>
        </Tooltip>
      )}

      <Divider orientation="vertical" className={styles.dividerMajor} />

      {visibleActions.map((action) => {
        const label = action.labelFor ? action.labelFor(ctx) : action.label;
        const iconName = action.iconFor ? action.iconFor(ctx) : action.icon;
        // Lock's icon: this branch only runs when !locked (locked
        // edges short-circuited above), so we always want the
        // "click-to-lock" affordance icon. Same logic as the node
        // toolbar — explicit beats implicit, and the registry's
        // iconFor would also resolve to lock_open here.
        const renderedIcon = action.id === 'lock' ? 'lock_open' : iconName;
        // Surface-specific aria-label kept for Delete: existing
        // tests assert "Delete edge" copy (kind-disambiguated).
        const ariaLabel = action.id === 'delete' ? 'Delete edge' : label;
        return (
          <Tooltip
            key={action.id}
            title={tooltipWithShortcut(label, edgeShortcutFor(action.id))}
            placement="top"
          >
            <IconButton
              transparent
              aria-label={ariaLabel}
              className={styles.btn}
              onClick={() => action.run(ctx)}
            >
              <Icon name={renderedIcon} size={icon.md} />
            </IconButton>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * Action IDs surfaced on the floating edge toolbar. STRICT SUBSET of
 * EDGE_ACTIONS — z-order lives in the right-click menu only, matching
 * how the node toolbar handles its z-order entries.
 */
const TOOLBAR_EDGE_ACTION_IDS: ReadonlySet<EdgeAction['id']> = new Set([
  'lock',
  'delete',
]);

/** Map registry IDs → canvasShortcuts label fns for tooltip extra. */
function edgeShortcutFor(id: EdgeAction['id']): string {
  switch (id) {
    case 'bring-to-front': return canvasShortcuts.bringToFront();
    case 'bring-forward': return canvasShortcuts.bringForward();
    case 'send-backward': return canvasShortcuts.sendBackward();
    case 'send-to-back': return canvasShortcuts.sendToBack();
    case 'lock': return canvasShortcuts.lock();
    case 'delete': return canvasShortcuts.delete();
  }
}
